import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Product } from '@/types/product';
import featuredProductsSnapshot from '@/data/featured-products-snapshot.json';

/**
 * Hook: useFeaturedProducts
 * Fetches active, in-stock products with at least one homepage flag.
 * Optimized for boolean database fields.
 *
 * placeholderData ships a build-time snapshot of this exact query (see
 * scripts/snapshot-featured-products.ts) so the homepage can paint real product cards
 * immediately on mount instead of waiting on this query's live round trip — Lighthouse's LCP
 * breakdown confirmed that wait (query round-trip, then the resulting image fetch only
 * starting once the URL is known) as the dominant cost for the homepage's LCP element. The
 * snapshot is shown only until this query resolves, then fully replaced — never a cache hit.
 */
export const useFeaturedProducts = (options: { enabled?: boolean } = {}) => {
  const { enabled = true } = options;

  return useQuery<Product[]>({
    queryKey: ['featured-products'],
    enabled,
    placeholderData: featuredProductsSnapshot.products as unknown as Product[],
    queryFn: async () => {
      try {
        // Fetch active products that have ANY of the homepage flags
        const { data, error } = await supabase
          .from('products')
          .select(`
            id,
            product_name,
            website_title,
            ws_code,
            stock_code,
            product_size,
            product_unit,
            excl_price,
            incl_price,
            special_price,
            is_active,
            is_top_seller,
            is_special_discount,
            is_favorite,
            is_new_arrival,
            stock_available,
            created_at,
            product_images (
              id,
              image_url,
              seo_image_url,
              alt_text,
              image_order
            )
          `)
          .eq('is_active', true)
          .eq('stock_available', true)
          .or('is_top_seller.eq.true,is_special_discount.eq.true,is_favorite.eq.true,is_new_arrival.eq.true')
          .order('created_at', { ascending: false })
          .order('image_order', { foreignTable: 'product_images' })
          .limit(50);

        if (error) {
          console.error('Supabase query error:', error);
          throw error;
        }

        if (!data) {
          console.warn('No data returned from Supabase');
          return [] as Product[];
        }

        return data as Product[];
      } catch (error) {
        console.error('Error in useFeaturedProducts:', error);
        return [] as Product[];
      }
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};